import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { TimelineStatus, type PurchaseOrderTimeline } from '@/components/delivery-timeline'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { FoldToggle } from './fold-toggle'
import {
  buildExpectedTimeline,
  buildProjectBar,
  dayNumber,
  weekdayLabel,
  type Row,
  type Rule,
  type Segment,
  type Status,
  type Tick,
} from './project-expected-timeline-model'

/** With the manufacturer is the status hue, in transit a lighter shade of it. */
const TONES: Record<Status, { lead: string; transit: string; note: string }> = {
  on_track: {
    lead: 'bg-sky-600 dark:bg-sky-500',
    transit: 'bg-sky-300 dark:bg-sky-700',
    note: '',
  },
  at_risk: {
    lead: 'bg-amber-500',
    transit: 'bg-amber-300 dark:bg-amber-700',
    note: 'text-amber-700 dark:text-amber-300',
  },
  late: {
    lead: 'bg-red-600 dark:bg-red-500',
    transit: 'bg-red-300 dark:bg-red-700',
    note: 'text-red-700 dark:text-red-300',
  },
  delivered: {
    lead: 'bg-zinc-400 dark:bg-zinc-500',
    transit: 'bg-zinc-300 dark:bg-zinc-600',
    note: '',
  },
}

const WAITING = 'bg-zinc-200 dark:bg-zinc-700'
/** Waiting on the spec credit request: its own violet, hatched like any wait to send. */
const SPEC =
  'border border-violet-400/80 bg-[repeating-linear-gradient(135deg,var(--color-violet-400)_0_1.5px,var(--color-violet-100)_1.5px_4px)] dark:border-violet-500 dark:bg-[repeating-linear-gradient(135deg,var(--color-violet-500)_0_1.5px,var(--color-violet-950)_1.5px_4px)]'
/** Three or more days before the PO went out: time lost, so it is hatched. */
const HATCH =
  'bg-zinc-100 bg-[repeating-linear-gradient(135deg,var(--color-zinc-400)_0_1.5px,transparent_1.5px_4px)] dark:bg-zinc-800 dark:bg-[repeating-linear-gradient(135deg,var(--color-zinc-500)_0_1.5px,transparent_1.5px_4px)]'

const STATUS_LABEL: Record<Status, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  late: 'Late',
  delivered: 'Delivered',
}

function segmentClass(segment: Segment, status: Status) {
  if (segment.kind === 'waiting') return segment.long ? HATCH : WAITING
  if (segment.kind === 'spec') return SPEC
  return TONES[status][segment.kind]
}

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

function day(value: string) {
  return weekdayLabel(dayNumber(value))
}

/** The dates behind one row, as label/value pairs for the tooltip and the screen-reader summary. */
function rowFacts(row: Row): [string, string][] {
  const t = row.timeline
  const delivered = t.status === 'delivered'
  const est = (value: string | null | undefined, firm: boolean) => (value ? `${day(value)}${firm ? '' : ' est.'}` : 'Not set')
  const facts: [string, string][] = [['Ordered', day(t.ordered_on)]]
  if (t.spec_credit_sent_on && row.specDays !== null) {
    facts.push(['Spec credit', `${day(t.spec_credit_sent_on)} (${plural(row.specDays, 'day')} waiting)`])
  } else if (t.spec_credit_deadline_on && row.specDays !== null) {
    facts.push(['Spec credit', `Reply due ${day(t.spec_credit_deadline_on)}`])
  }
  if (t.sent_on) {
    facts.push(['Sent', `${day(t.sent_on)}${row.waitDays > 0 ? ` (+${plural(row.waitDays, 'day')})` : ''}`])
  } else if (!delivered) {
    facts.push(['Sent', row.expectedSend !== null ? `Not yet (expected ${weekdayLabel(row.expectedSend)})` : 'Not yet'])
  }
  const ship = t.expected_ship_on ?? t.planned_ship_on
  facts.push([t.shipped || delivered ? 'Shipped' : 'Ships', est(ship, t.firm || t.shipped || delivered)])
  const arrival = t.expected_arrival_on ?? t.planned_arrival_on
  facts.push([delivered ? 'Arrived' : 'Arrives', est(arrival, t.firm || delivered)])
  if (row.plannedArrival !== null) facts.push(['Planned', weekdayLabel(row.plannedArrival)])
  if (t.deliver_by) {
    const late = row.daysLate !== null && row.daysLate > 0 && !delivered ? ` (${plural(row.daysLate, 'day')} late)` : ''
    facts.push(['Deliver by', `${day(t.deliver_by)}${late}`])
  }
  return facts
}

function rowName(t: PurchaseOrderTimeline) {
  return [t.manufacturer_name, t.po_number].filter(Boolean).join(' ')
}

/** Week or month gridlines plus the Today and deliver-by rules, behind one row's bar. */
function Gridlines({ ticks, today, deliverBy }: { ticks: Tick[]; today: Rule | null; deliverBy: Rule | null }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {ticks.map((tick) => (
        <span key={tick.day} className="absolute inset-y-0 border-l border-border/70" style={{ left: `${tick.left}%` }} />
      ))}
      {deliverBy ? (
        <span
          className="absolute inset-y-0 border-l border-dashed border-foreground/50"
          style={{ left: `${deliverBy.left}%` }}
        />
      ) : null}
      {today ? (
        <span className="absolute inset-y-0 w-px bg-foreground/60" style={{ left: `${today.left}%` }} />
      ) : null}
    </div>
  )
}

function ArrivalCell({ row }: { row: Row }) {
  const { arrival, timeline } = row
  return (
    <span className="flex flex-wrap items-baseline justify-end gap-x-1.5 whitespace-nowrap text-sm tabular-nums sm:justify-start sm:pl-4">
      <span className={cn(timeline.status === 'delivered' && 'text-muted-foreground')}>
        {arrival.text}
        {arrival.est ? <span className="ml-1 text-xs text-muted-foreground">est.</span> : null}
      </span>
      {arrival.note ? (
        <span className={cn('text-xs font-medium', TONES[timeline.status].note)}>{arrival.note}</span>
      ) : null}
    </span>
  )
}

type Axis = Pick<ReturnType<typeof buildExpectedTimeline>, 'ticks' | 'today' | 'deliverBy'>

function TimelineRow({ row, axis, showPoNumber }: { row: Row; axis: Axis; showPoNumber: boolean }) {
  const t = row.timeline
  const facts = rowFacts(row)
  const navigate = useNavigate()
  const openPo = () => navigate({ to: '/purchase-orders/$poId', params: { poId: t.purchase_order_uuid } })
  const name = t.manufacturer_name || t.po_number
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 pt-2 sm:col-span-full sm:grid-cols-subgrid sm:gap-x-0 sm:pt-0">
      <Link
        to="/purchase-orders/$poId"
        params={{ poId: t.purchase_order_uuid }}
        title={t.po_number}
        aria-label={rowName(t)}
        className="min-w-0 truncate text-sm hover:underline sm:max-w-60 sm:pr-4"
      >
        {name}
        {showPoNumber && t.manufacturer_name ? (
          <span className="ml-1.5 text-xs tabular-nums text-muted-foreground">{t.po_number}</span>
        ) : null}
      </Link>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="img"
            tabIndex={0}
            aria-label={`${rowName(t)}, ${STATUS_LABEL[t.status]}: ${facts.map(([k, v]) => `${k} ${v}`).join(', ')}`}
            onClick={openPo}
            onKeyDown={(event) => {
              if (event.key === 'Enter') openPo()
            }}
            className="relative col-span-2 row-start-2 h-6 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring/50 sm:col-span-1 sm:row-start-auto sm:h-9"
          >
            <Gridlines {...axis} />
            {row.segments.map((segment) => (
              <div
                key={`${segment.kind}-${segment.forecast ? 'forecast' : 'past'}`}
                data-segment={segment.kind}
                data-forecast={segment.forecast ? 'true' : undefined}
                data-long={segment.long ? 'true' : undefined}
                className={cn(
                  'absolute top-1/2 h-3 -translate-y-1/2',
                  segmentClass(segment, t.status),
                  segment.forecast && 'opacity-40',
                )}
                style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
              />
            ))}
            {row.markers.map((marker) => (
              <span
                key={marker.kind}
                aria-hidden
                data-marker={marker.kind}
                className="absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-card"
                style={{ left: `${marker.left}%` }}
              />
            ))}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-left">
          <p className="mb-1 font-medium">{rowName(t)}</p>
          <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
            {facts.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="opacity-70">{label}</dt>
                <dd className="tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </TooltipContent>
      </Tooltip>
      <span className="col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto">
        <ArrivalCell row={row} />
      </span>
    </li>
  )
}

function RuleLabel({ rule, className }: { rule: Rule; className?: string }) {
  return (
    <span
      className={cn(
        'absolute top-0 whitespace-nowrap px-1 text-[11px] font-medium leading-4',
        rule.align === 'end' && '-translate-x-full',
        className,
      )}
      style={{ left: `${rule.left}%` }}
    >
      {rule.label}
    </span>
  )
}

function Swatch({ className }: { className: string }) {
  return <span aria-hidden className={cn('inline-block h-2.5 w-3.5', className)} />
}

/** The legend shows the hue most rows use: on track's, else whichever status is shown. */
function legendStatus(rows: Row[]): Status {
  const shown = new Set(rows.map((row) => row.timeline.status))
  return (['on_track', 'at_risk', 'late', 'delivered'] as const).find((status) => shown.has(status)) ?? 'on_track'
}

const SHELL = 'rounded-lg border bg-card'
/** Rows shown before "Show all N manufacturers". */
const ROW_FOLD = 6

/** Every PO's path from ordered to delivered, against today and the customer's date (USA-1432). */
export function ProjectExpectedTimeline({
  timelines,
  isLoading,
}: {
  timelines: PurchaseOrderTimeline[]
  isLoading?: boolean
}) {
  const [showDelivered, setShowDelivered] = useState(false)
  const [showAll, setShowAll] = useState(false)

  if (isLoading && !timelines.length) {
    return (
      <section aria-label="Expected timeline" aria-busy className="space-y-2">
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Expected timeline</h2>
        <div className={cn(SHELL, 'space-y-2 p-3')} data-testid="expected-timeline-loading">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-4 w-full" />
          ))}
        </div>
      </section>
    )
  }
  if (!timelines.length) return null

  const chart = buildExpectedTimeline(timelines)
  const deliveredCount = chart.rows.filter((row) => row.timeline.status === 'delivered').length
  const foldable = deliveredCount > 0 && deliveredCount < chart.rows.length
  const listed = foldable && !showDelivered ? chart.rows.filter((row) => row.timeline.status !== 'delivered') : chart.rows
  // Past six rows the rest fold away (USA-1432); sorted by arrival, so the last to arrive folds first.
  const rows = showAll ? listed : listed.slice(0, ROW_FOLD)
  const perManufacturer = new Map<string, number>()
  for (const t of timelines) {
    if (t.manufacturer_name) perManufacturer.set(t.manufacturer_name, (perManufacturer.get(t.manufacturer_name) ?? 0) + 1)
  }
  const legendTone = TONES[legendStatus(rows)]
  const showsWaiting = rows.some((row) => row.segments.some((s) => s.kind === 'waiting'))
  const showsHatch = rows.some((row) => row.segments.some((s) => s.long))
  const showsSpec = rows.some((row) => row.segments.some((s) => s.kind === 'spec'))
  const showsForecast = rows.some((row) => row.segments.some((s) => s.forecast))
  const axis: Axis = { ticks: chart.ticks, today: chart.today, deliverBy: chart.deliverBy }

  return (
    <section aria-labelledby="project-expected-timeline-title" className="space-y-2">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <h2
          id="project-expected-timeline-title"
          className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
        >
          Expected timeline
        </h2>
        {chart.summary ? (
          <p className="col-span-2 row-start-2 text-xs text-muted-foreground sm:col-span-1 sm:row-start-auto sm:truncate">
            {chart.summary}
          </p>
        ) : null}
        <span className="col-start-2 row-start-1 sm:col-start-3">
          <TimelineStatus status={chart.worst} />
        </span>
      </div>
      <TooltipProvider>
        <div className={SHELL}>
          <div className="overflow-x-hidden rounded-t-lg">
            <div className="grid grid-cols-1 px-3 pb-2 sm:grid-cols-[minmax(0,max-content)_minmax(0,1fr)_max-content]">
              {/* Axis: rule labels over tick labels. */}
              <div className="grid h-10 grid-cols-1 bg-card pt-1.5 sm:col-span-full sm:grid-cols-subgrid">
                <span className="hidden sm:block" />
                <div className="relative">
                  {chart.today ? <RuleLabel rule={chart.today} className="text-foreground" /> : null}
                  {chart.deliverBy ? (
                    <RuleLabel rule={chart.deliverBy} className="text-foreground" />
                  ) : null}
                  {chart.ticks.map((tick) => (
                    <span
                      key={tick.day}
                      aria-hidden
                      className={cn(
                        'absolute top-4 whitespace-nowrap border-l pl-1 text-[11px] leading-4 tabular-nums text-muted-foreground',
                        // Phones: every other tick, and none hard against the right edge.
                        (!tick.major || tick.left > 85) && 'max-sm:hidden',
                      )}
                      style={{ left: `${tick.left}%` }}
                    >
                      {tick.label}
                    </span>
                  ))}
                </div>
                <span className="hidden self-end pb-0.5 pl-4 text-[11px] font-medium text-muted-foreground sm:block">
                  Arrives
                </span>
              </div>
              <ul className="grid grid-cols-1 sm:col-span-full sm:grid-cols-subgrid">
                {rows.map((row) => (
                  <TimelineRow
                    key={row.timeline.purchase_order_uuid}
                    row={row}
                    axis={axis}
                    showPoNumber={(perManufacturer.get(row.timeline.manufacturer_name ?? '') ?? 0) > 1}
                  />
                ))}
              </ul>
            </div>
            {listed.length > ROW_FOLD ? (
              <div className="px-2 pb-1.5">
                <FoldToggle
                  expanded={showAll}
                  total={listed.length}
                  noun="manufacturers"
                  onToggle={() => setShowAll((value) => !value)}
                />
              </div>
            ) : null}
          </div>
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-3 py-1.5 text-[11px] text-muted-foreground"
            data-testid="expected-timeline-legend"
          >
            {showsWaiting ? (
              <span className="inline-flex items-center gap-1">
                <Swatch className={showsHatch ? HATCH : WAITING} /> Waiting to send
              </span>
            ) : null}
            {showsSpec ? (
              <span className="inline-flex items-center gap-1">
                <Swatch className={SPEC} /> Spec credit wait
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <Swatch className={legendTone.lead} /> At manufacturer
            </span>
            <span className="inline-flex items-center gap-1">
              <Swatch className={legendTone.transit} /> In transit
            </span>
            {showsForecast ? <span>Faded = expected</span> : null}
            {foldable ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="-my-1 ml-auto text-[11px] text-muted-foreground"
                aria-expanded={showDelivered}
                onClick={() => setShowDelivered((value) => !value)}
              >
                {showDelivered ? 'Hide delivered' : `Show ${deliveredCount} delivered`}
              </Button>
            ) : null}
          </div>
        </div>
      </TooltipProvider>
    </section>
  )
}

/** One PO's bar on its own axis, for the PO page's delivery timeline card (USA-1432). */
export function PurchaseOrderTimelineBar({ timeline }: { timeline: PurchaseOrderTimeline }) {
  const chart = buildExpectedTimeline([timeline])
  const row = chart.rows[0]
  if (!row) return null
  const tone = TONES[timeline.status]
  const facts = rowFacts(row)
  return (
    <div data-testid="po-timeline-bar" className="space-y-1">
      <div className="relative h-8">
        {chart.today ? <RuleLabel rule={chart.today} className="text-foreground" /> : null}
        {chart.deliverBy ? <RuleLabel rule={chart.deliverBy} className="text-foreground" /> : null}
        {chart.ticks.map((tick) => (
          <span
            key={tick.day}
            aria-hidden
            className={cn(
              'absolute top-4 whitespace-nowrap border-l pl-1 text-[11px] leading-4 tabular-nums text-muted-foreground',
              tick.left > 88 && 'hidden',
            )}
            style={{ left: `${tick.left}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>
      <div
        role="img"
        aria-label={`${STATUS_LABEL[timeline.status]}: ${facts.map(([k, v]) => `${k} ${v}`).join(', ')}`}
        className="relative h-6"
      >
        <Gridlines ticks={chart.ticks} today={chart.today} deliverBy={chart.deliverBy} />
        {row.segments.map((segment) => (
          <div
            key={`${segment.kind}-${segment.forecast ? 'forecast' : 'past'}`}
            data-segment={segment.kind}
            data-forecast={segment.forecast ? 'true' : undefined}
            className={cn(
              'absolute top-1/2 h-3 -translate-y-1/2',
              segmentClass(segment, timeline.status),
              segment.forecast && 'opacity-40',
            )}
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
          />
        ))}
        {row.markers.map((marker) => (
          <span
            key={marker.kind}
            aria-hidden
            className="absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-card"
            style={{ left: `${marker.left}%` }}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        {row.segments.some((s) => s.kind === 'waiting') ? (
          <span className="inline-flex items-center gap-1">
            <Swatch className={row.segments.some((s) => s.long) ? HATCH : WAITING} /> Waiting to send
          </span>
        ) : null}
        {row.segments.some((s) => s.kind === 'spec') ? (
          <span className="inline-flex items-center gap-1">
            <Swatch className={SPEC} /> Spec credit wait
            {row.specDays !== null && row.specDays > 0 ? ` (${plural(row.specDays, 'day')})` : null}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <Swatch className={tone.lead} /> At manufacturer
        </span>
        <span className="inline-flex items-center gap-1">
          <Swatch className={tone.transit} /> In transit
        </span>
      </div>
    </div>
  )
}

/** Rows listed in the table bar's tooltip before "and N more". */
const TOOLTIP_ROWS = 8

function arrivalLine(row: Row, note: string | null) {
  const { arrival, timeline } = row
  return (
    <>
      {timeline.status === 'delivered' || arrival.text === 'No date yet' ? arrival.text : `Arrives ${arrival.text}`}
      {arrival.est ? <span className="ml-1 text-muted-foreground">est.</span> : null}
      {note ? <span className={cn('ml-1.5 font-medium', TONES[timeline.status].note)}>{note}</span> : null}
    </>
  )
}

/**
 * The projects table's timeline cell (USA-1498): one PO's bar with when it arrives, or for a
 * project with several POs, all of them on one bar with a mark where each arrives.
 */
export function ProjectTimelineBar({ timelines }: { timelines: PurchaseOrderTimeline[] }) {
  const bar = buildProjectBar(timelines)
  if (!bar) return <span className="text-xs text-muted-foreground">-</span>
  const single = bar.rows.length === 1
  const { last } = bar
  // Several POs: the worst status speaks for the project; the last arrival is when it is all there.
  const note = single ? last.arrival.note : bar.worst === 'late' ? 'late' : bar.worst === 'at_risk' ? 'at risk' : null
  const lastStatus = single ? last.timeline.status : bar.worst
  const past = bar.segments.filter((segment) => !segment.forecast)
  const forecast = bar.segments.filter((segment) => segment.forecast)
  const facts = single ? rowFacts(last) : null
  const label = single
    ? `${rowName(last.timeline)}, ${STATUS_LABEL[last.timeline.status]}: ${facts!.map(([k, v]) => `${k} ${v}`).join(', ')}`
    : `${bar.rows.length} purchase orders, ${STATUS_LABEL[bar.worst]}: ${bar.rows
        .map((row) => `${rowName(row.timeline)} ${row.arrival.text}`)
        .join(', ')}`
  const segment = (item: (typeof bar.segments)[number]) => (
    <div
      key={`${item.kind}-${item.start}-${item.end}-${item.status}`}
      data-segment={item.kind}
      className={cn('absolute inset-y-0', segmentClass(item, item.status))}
      style={{ left: `${item.left}%`, width: `${item.width}%` }}
    />
  )
  return (
    <div data-testid="project-timeline-bar" className="w-full min-w-36 space-y-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              role="img"
              tabIndex={0}
              aria-label={label}
              className="relative h-4 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <div className="absolute inset-x-0 top-1 h-2 overflow-hidden rounded-[2px] bg-muted">
                {past.map(segment)}
                {/* Faded as one layer, so overlapping POs do not darken each other. */}
                <div className="absolute inset-0 opacity-40">{forecast.map(segment)}</div>
              </div>
              {bar.deliverBy !== null ? (
                <span
                  aria-hidden
                  className="absolute inset-y-0 border-l border-dashed border-foreground/50"
                  style={{ left: `${bar.deliverBy}%` }}
                />
              ) : null}
              {bar.today !== null ? (
                <span
                  aria-hidden
                  data-testid="project-timeline-today"
                  className="absolute inset-y-0 w-px bg-foreground/70"
                  style={{ left: `${bar.today}%` }}
                />
              ) : null}
              {bar.arrivals.map((arrival, index) => (
                <span
                  key={index}
                  aria-hidden
                  data-arrival={arrival.delivered ? 'delivered' : 'expected'}
                  className={cn(
                    'absolute top-0.5 h-3 w-0.5 -translate-x-1/2 rounded-full ring-1 ring-card',
                    arrival.delivered ? 'bg-zinc-500' : 'bg-foreground/80',
                  )}
                  style={{ left: `${arrival.left}%` }}
                />
              ))}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-left">
            {facts ? (
              <>
                <p className="mb-1 font-medium">{rowName(last.timeline)}</p>
                <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
                  {facts.map(([key, value]) => (
                    <div key={key} className="contents">
                      <dt className="opacity-70">{key}</dt>
                      <dd className="tabular-nums">{value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : (
              <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
                {bar.rows.slice(0, TOOLTIP_ROWS).map((row) => (
                  <div key={row.timeline.purchase_order_uuid} className="contents">
                    <dt className="opacity-70">{row.timeline.manufacturer_name || row.timeline.po_number}</dt>
                    <dd className="tabular-nums">
                      {row.arrival.text}
                      {row.arrival.est ? ' est.' : ''}
                      {row.arrival.note ? ` · ${row.arrival.note}` : ''}
                    </dd>
                  </div>
                ))}
                {bar.rows.length > TOOLTIP_ROWS ? (
                  <p className="col-span-2 opacity-70">and {bar.rows.length - TOOLTIP_ROWS} more</p>
                ) : null}
              </dl>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <p className="text-xs tabular-nums">
        {arrivalLine({ ...last, timeline: { ...last.timeline, status: lastStatus } }, note)}
      </p>
      {single ? null : (
        <p className="text-[11px] text-muted-foreground">
          {bar.delivered && bar.delivered < bar.rows.length
            ? `${bar.delivered} of ${bar.rows.length} POs delivered`
            : `${bar.rows.length} POs`}
        </p>
      )}
    </div>
  )
}
